window.ebizDateInput = window.ebizDateInput || {};

// Registry of active overlays keyed by input id, so refresh()/dispose() can find them.
window.ebizDateInput._instances = window.ebizDateInput._instances || {};

// Mask characters used by the Telerik DateInputFormatPlaceholder (M/D/Y), case-insensitive.
const EBIZ_DATE_MASK_CHARS = new Set(["m", "d", "y"]);

function ebizDateIsMaskChar(ch) {
    return EBIZ_DATE_MASK_CHARS.has(ch.toLowerCase());
}

function ebizDateEscapeHtml(ch) {
    switch (ch) {
        case "&": return "&amp;";
        case "<": return "&lt;";
        case ">": return "&gt;";
        case '"': return "&quot;";
        case "'": return "&#39;";
        default: return ch;
    }
}

// A value character belongs to a date "field" if it's a typed digit or an untyped mask letter
// (M/D/Y); everything else (slashes, dashes, spaces) is a separator.
function ebizDateValueIsFieldChar(ch) {
    return ebizDateIsMaskChar(ch) || (ch >= "0" && ch <= "9");
}

// Tokenizes the live value into ordered field/sep segments, the same shape ebizDateTokenize
// produces for a format. Working off the value itself keeps separator coloring correct even when
// the masked value length doesn't match the format length (e.g. a 2-char "YY" year placeholder
// under a 4-char "yyyy" format), which would otherwise leave every separator muted.
function ebizDateTokenizeValue(value) {
    const tokens = [];
    let i = 0;
    while (i < value.length) {
        const isField = ebizDateValueIsFieldChar(value[i]);
        let j = i + 1;
        while (j < value.length && ebizDateValueIsFieldChar(value[j]) === isField) {
            j++;
        }
        tokens.push({ type: isField ? "field" : "sep", start: i, end: j });
        i = j;
    }
    return tokens;
}

// True when every character of the given field token has been typed (no mask letters remain).
function ebizDateFieldIsFilled(field, value) {
    for (let k = field.start; k < field.end && k < value.length; k++) {
        if (ebizDateIsMaskChar(value[k])) {
            return false;
        }
    }
    return field.start < value.length;
}

// The Telerik date input keeps the whole masked string (e.g. "07/DD/YYYY") in its live value,
// so we only decide a color per character:
//   - mask letter (M/D/Y)  -> "mask" (muted)
//   - digit                -> "typed"
//   - separator            -> "typed" only once the field to its LEFT is fully typed, otherwise
//                             "mask" (slashes stay muted until their left section is complete).
function ebizDateClassFor(index, value, tokens) {
    const ch = value[index];
    if (ebizDateIsMaskChar(ch)) {
        return "ebiz-dateinput-mask";
    }
    if (ch >= "0" && ch <= "9") {
        return "ebiz-dateinput-typed";
    }

    // Separator: find the field token immediately to its left; muted until that field is filled.
    for (let t = tokens.length - 1; t >= 0; t--) {
        if (tokens[t].type === "field" && tokens[t].end <= index) {
            return ebizDateFieldIsFilled(tokens[t], value) ? "ebiz-dateinput-typed" : "ebiz-dateinput-mask";
        }
    }
    return "ebiz-dateinput-mask";
}

window.ebizDateInput.init = function (inputId, overlayId) {
    const noop = { dispose: function () { }, render: function () { } };

    const input = document.getElementById(inputId);
    const overlay = document.getElementById(overlayId);
    if (!input || !overlay) {
        // Return a JS object reference (not a plain object) so Blazor can marshal it into the
        // IJSObjectReference the .NET side awaits and can safely call "dispose" on later.
        return DotNet.createJSObjectReference(noop);
    }

    const wrapper = overlay.parentElement;
    let rafId = null;
    let lastValue = null;

    function syncStyles() {
        const cs = getComputedStyle(input);
        overlay.style.fontFamily = cs.fontFamily;
        overlay.style.fontSize = cs.fontSize;
        overlay.style.fontWeight = cs.fontWeight;
        overlay.style.fontStyle = cs.fontStyle;
        overlay.style.letterSpacing = cs.letterSpacing;
        overlay.style.paddingTop = cs.paddingTop;
        overlay.style.paddingRight = cs.paddingRight;
        overlay.style.paddingBottom = cs.paddingBottom;
        overlay.style.paddingLeft = cs.paddingLeft;

        // Position the overlay over the input via the bounding-rect delta relative to the wrapper,
        // which is robust to Telerik's floating-label offset parent.
        const inputRect = input.getBoundingClientRect();
        const anchor = wrapper || overlay.offsetParent || input;
        const anchorRect = anchor.getBoundingClientRect();
        overlay.style.top = (inputRect.top - anchorRect.top) + "px";
        overlay.style.left = (inputRect.left - anchorRect.left) + "px";
        overlay.style.width = inputRect.width + "px";
        overlay.style.height = inputRect.height + "px";
    }

    function render() {
        const value = input.value || "";

        // Telerik selects the whole active segment (MM/DD/YYYY) while it's being edited; mirror that
        // so the selected characters render in the highlight text color instead of the muted/typed one.
        const focused = document.activeElement === input;
        const selStart = focused ? (input.selectionStart ?? -1) : -1;
        const selEnd = focused ? (input.selectionEnd ?? -1) : -1;

        const key = value + "|" + selStart + "|" + selEnd;
        if (key === lastValue) {
            return;
        }
        lastValue = key;

        // Tokenize the live value so separators color per-section as each field fills, regardless
        // of whether the masked value length matches the format length.
        const activeTokens = ebizDateTokenizeValue(value);

        let html = "";
        for (let i = 0; i < value.length; i++) {
            const cls = (i >= selStart && i < selEnd)
                ? "ebiz-dateinput-selected"
                : ebizDateClassFor(i, value, activeTokens);
            html += '<span class="' + cls + '">' + ebizDateEscapeHtml(value[i]) + "</span>";
        }
        overlay.innerHTML = html;
    }

    function stopRaf() {
        if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    // While focused, Telerik updates the value across async round-trips (and may not fire a native
    // 'input' event for programmatic changes), so reconcile every frame until blur.
    function rafLoop() {
        render();
        rafId = requestAnimationFrame(rafLoop);
    }

    function onFocus() {
        syncStyles();
        render();
        stopRaf();
        rafId = requestAnimationFrame(rafLoop);
    }

    function onBlur() {
        stopRaf();
        // Telerik clears an incomplete value on blur over one or more round-trips; repaint after
        // it settles so the overlay empties and the native floating label can drop.
        requestAnimationFrame(render);
        setTimeout(render, 50);
    }

    input.addEventListener("input", render);
    input.addEventListener("focus", onFocus);
    input.addEventListener("blur", onBlur);
    window.addEventListener("resize", syncStyles);

    // Initial paint for any programmatic starting value.
    syncStyles();
    render();

    const instance = {
        render: function () {
            lastValue = null; // force a repaint on external refresh
            render();
        },
        dispose: function () {
            stopRaf();
            input.removeEventListener("input", render);
            input.removeEventListener("focus", onFocus);
            input.removeEventListener("blur", onBlur);
            window.removeEventListener("resize", syncStyles);
            overlay.innerHTML = "";
            delete window.ebizDateInput._instances[inputId];
        }
    };

    window.ebizDateInput._instances[inputId] = instance;
    // Return a JS object reference so Blazor can marshal it into IJSObjectReference and the .NET
    // side can invoke "dispose" on it during component disposal.
    return DotNet.createJSObjectReference(instance);
};

// Repaint an overlay from Blazor when its value changes programmatically (no-op if unknown).
window.ebizDateInput.refresh = function (inputId) {
    const instance = window.ebizDateInput._instances[inputId];
    if (instance) {
        instance.render();
    }
};
